import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import './servermonitor.css';
import '../backlog/backlog.css';   // shared .sa-filter-btn chips (BO UI Standard)
import { BusyIcon } from '../../components/loading';
import { getToken } from '../../auth/store';
import { fmtLocal } from '../../utils/datetime';
import { useNotify } from '../../components/notifications';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';

// ─────────────────────────────────────────────────────────────────────────
// Server Monitor → History page
// ─────────────────────────────────────────────────────────────────────────
//   • Time range presets (Last hour / Today / Yesterday / 7 days / 30 days / Custom)
//   • Mini line charts: CPU %, Memory %, Memory GB over the selected range
//   • Snapshot table — every captured row
//   • Download buttons: CSV / Excel / PDF
//
// Data source: GET /api/v1/server/history?from=&to=
// All times are UTC on the wire; the page converts to the user's local
// time for display.
// ─────────────────────────────────────────────────────────────────────────

interface SnapshotRow {
  oid: string;
  capturedAt: string;     // ISO UTC
  serverName: string | null;
  cpuPct: number | null;
  memUsedGb: number | null;
  memTotalGb: number | null;
  memPct: number | null;
  diskUsedGb: number | null;
  diskTotalGb: number | null;
  warnings: string | null;
  // The four JSON columns arrive as raw strings — parsed on demand when
  // the user expands a row.  Keeping them as strings makes the list query
  // cheap (no per-row deserialisation) until the user actually drills in.
  iisAppPoolsJson?:     string | null;
  xafRuntimesJson?:     string | null;
  memoryConsumersJson?: string | null;
  servicesJson?:        string | null;
}

interface IisAppPool      { appPool?: string; processId?: number; memoryGb?: number; percentOfTotal?: number; cpuPct?: number; uptime?: string }
interface XafRuntime      { appPool?: string; gen2GcCount?: number; lohSizeMb?: number; threadCount?: number; handleCount?: number; requestsPerSec?: number; errorsPerSec?: number }
interface MemoryConsumer  { name?: string; category?: string; memoryGb?: number; percentOfTotal?: number; processCount?: number }
interface ServiceStatus   { name?: string; isOnline?: boolean; details?: string }

function safeParse<T>(s: string | null | undefined): T[] {
  if (!s) return [];
  try { const v = JSON.parse(s); return Array.isArray(v) ? v as T[] : []; }
  catch { return []; }
}

// Date/time formatting helpers live in src/utils/datetime.ts so every
// page renders timestamps with one consistent rule (forceUtc + US,
// 24-hour clock in the viewer's local timezone).

interface Summary {
  fromUtc: string;
  toUtc: string;
  sampleCount: number;
  peakCpuPct: number;
  peakCpuAtUtc: string;
  peakMemPct: number;
  peakMemAtUtc: string;
  avgCpuPct: number;
  avgMemPct: number;
  warningCount: number;
  topMemoryConsumer: string | null;
}

type Preset = 'hour' | 'today' | 'yesterday' | '7d' | '30d' | 'custom';

function buildRange(preset: Preset, customFrom?: string, customTo?: string):
    { fromUtc: string; toUtc: string } {
  const now = new Date();
  const isoUtc = (d: Date) => d.toISOString();
  switch (preset) {
    case 'hour':
      return { fromUtc: isoUtc(new Date(now.getTime() - 60 * 60 * 1000)),
               toUtc: isoUtc(now) };
    case 'today': {
      const t = new Date(now); t.setHours(0, 0, 0, 0);
      return { fromUtc: isoUtc(t), toUtc: isoUtc(now) };
    }
    case 'yesterday': {
      const start = new Date(now); start.setDate(start.getDate() - 1); start.setHours(0,0,0,0);
      const end   = new Date(start); end.setDate(end.getDate() + 1);
      return { fromUtc: isoUtc(start), toUtc: isoUtc(end) };
    }
    case '7d':
      return { fromUtc: isoUtc(new Date(now.getTime() - 7  * 24 * 3600 * 1000)),
               toUtc: isoUtc(now) };
    case '30d':
      return { fromUtc: isoUtc(new Date(now.getTime() - 30 * 24 * 3600 * 1000)),
               toUtc: isoUtc(now) };
    case 'custom':
      return { fromUtc: customFrom ? new Date(customFrom).toISOString() : isoUtc(new Date(now.getTime() - 24 * 3600 * 1000)),
               toUtc:   customTo   ? new Date(customTo).toISOString()   : isoUtc(now) };
  }
}

// Tiny SVG line chart — no extra libs.  Takes pre-resolved [x, y] points
// where x is an epoch-ms timestamp.  Renders into a fixed viewBox so the
// SVG scales to whatever width the parent container is.
//
// Axes:
//   • Y axis (left)   — % values at 25 / 50 / 75 / 100
//   • X axis (bottom) — 5 evenly-spaced timestamps showing what the points
//     represent (format adapts: HH:mm for ≤ 1 day, dd-MM HH:mm for ≤ 7 days,
//     dd-MMM for longer ranges).  Users were confused with bare lines.
function MiniLineChart({ points, color, height = 110, max = 100, label }:
    { points: { x: number; y: number }[]; color: string;
      height?: number; max?: number; label?: string }) {
  // Internal coordinate space — SVG scales to fit any parent width via
  // preserveAspectRatio + viewBox.  Pick numbers large enough for smooth
  // curves at any monitor size.
  const VB_W = 1600;
  if (points.length === 0) return (
    <div style={{ width: '100%', height, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#bbb', fontSize: 12,
                  border: '1px dashed #ddd', borderRadius: 4 }}>
      No samples in this range
    </div>);
  const padX = 32, padY = 8;
  const axisH = 16;                 // reserved room at the bottom for X labels
  const plotH = height - axisH;
  const xs = points.map(p => p.x);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const xSpan = xMax - xMin || 1;
  const px = (x: number) => padX + ((x - xMin) / xSpan) * (VB_W - padX - 4);
  const py = (y: number) => plotH - padY - (y / max) * (plotH - padY * 2);
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${px(p.x).toFixed(1)} ${py(p.y).toFixed(1)}`).join(' ');

  // X-axis tick formatter — adapts to the visible range.
  // chart-axis label — permitted §18.6 exception: kept inline, but US locale +
  // 24-hour (hour12:false) and MM-DD order to match the rest of the app
  // (MM/DD/YYYY, never AM/PM).
  const dayMs = 86_400_000;
  const fmtTick = (ms: number) => {
    const d2 = new Date(ms);
    if (xSpan <= dayMs)
      return d2.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    if (xSpan <= 7 * dayMs)
      return `${String(d2.getMonth() + 1).padStart(2, '0')}-${String(d2.getDate()).padStart(2, '0')} ` +
             d2.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    return d2.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
  };
  const tickCount = 5;
  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const t = xMin + (i / (tickCount - 1)) * xSpan;
    return { x: t, label: fmtTick(t) };
  });

  return (
    <svg viewBox={`0 0 ${VB_W} ${height}`} preserveAspectRatio="none"
         style={{ display: 'block', width: '100%', height }}>
      {/* Y-axis gridlines at 25/50/75/100% */}
      {[0.25, 0.5, 0.75, 1].map(f => (
        <line key={f} x1={padX} x2={VB_W - 4}
              y1={py(max * f)} y2={py(max * f)}
              stroke="#eef2f6" strokeWidth={1} strokeDasharray="2,2" />
      ))}
      {[0.25, 0.5, 0.75, 1].map(f => (
        <text key={'t' + f} x={4} y={py(max * f) + 3}
              fontSize="9" fill="#999">
          {(max * f).toFixed(0)}{label?.includes('%') ? '%' : ''}
        </text>
      ))}
      <path d={d} fill="none" stroke={color} strokeWidth={1.5}
            strokeLinecap="round" strokeLinejoin="round"
            vectorEffect="non-scaling-stroke" />
      {/* X-axis baseline + ticks */}
      <line x1={padX} x2={VB_W - 4} y1={plotH} y2={plotH}
            stroke="#cbd5e1" strokeWidth={1} />
      {ticks.map((t, i) => (
        <line key={'tk' + i} x1={px(t.x)} x2={px(t.x)}
              y1={plotH} y2={plotH + 3}
              stroke="#94a3b8" strokeWidth={1} />
      ))}
      {/* X-axis labels — anchor first/last inside the plot edges so they
          don't clip; middle ticks center on their tick mark. */}
      {ticks.map((t, i) => (
        <text key={'tl' + i}
              x={px(t.x)} y={height - 3}
              fontSize="9" fill="#64748b"
              textAnchor={i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle'}>
          {t.label}
        </text>
      ))}
    </svg>
  );
}

// ── Detail panel rendered inside a snapshot row when expanded ────────
// Shows the same four breakdowns the live Server Monitor page shows:
//   • IIS App Pools      (memory + CPU per pool)
//   • Top Memory Consumers (process / category)
//   • XAF .NET runtimes  (GC, LOH, threads, handles, req/s)
//   • Services           (online / offline)
// All four arrive as JSON strings on the snapshot row and are parsed lazily.
function SnapshotDetail({ row }: { row: SnapshotRow }) {
  const pools     = safeParse<IisAppPool>(row.iisAppPoolsJson);
  const consumers = safeParse<MemoryConsumer>(row.memoryConsumersJson);
  const runtimes  = safeParse<XafRuntime>(row.xafRuntimesJson);
  const services  = safeParse<ServiceStatus>(row.servicesJson);

  const cardStyle: React.CSSProperties = {
    background: 'var(--ai-card)', border: '1px solid var(--ai-line)', borderRadius: 10,
    boxShadow: 'var(--ai-shadow-card)',
    padding: '8px 12px',
  };
  const titleStyle: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: '#1F4E79', marginBottom: 6,
    textTransform: 'uppercase', letterSpacing: 0.4,
  };
  const cellNum: React.CSSProperties = {
    padding: '3px 8px', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', color: '#444',
  };
  const cellTxt: React.CSSProperties = { padding: '3px 8px', textAlign: 'left' };
  const bar = (pct: number, color = '#F97316') => (
    <div style={{ width: 80, height: 5, background: '#eef2f6', borderRadius: 2,
                  display: 'inline-block', verticalAlign: 'middle', marginLeft: 6 }}>
      <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%',
                    background: color, borderRadius: 2 }} />
    </div>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                  gap: 10, fontSize: 11 }}>
      {/* IIS App Pools */}
      <div style={cardStyle}>
        <div style={titleStyle}>
          <i className="ti ti-server-2" style={{ marginRight: 4, color: '#2563eb' }} />
          IIS App Pools ({pools.length})
        </div>
        {pools.length === 0 ? (
          <div style={{ color: '#999', fontSize: 11 }}>No IIS pools recorded for this snapshot.</div>
        ) : (
          <table className="smon-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: '#666' }}>
              <th style={cellTxt}>App pool</th>
              <th style={cellNum}>Mem GB</th>
              <th style={cellNum}>% host</th>
              <th style={cellNum}>CPU %</th>
              <th style={cellNum}>PID</th>
            </tr></thead>
            <tbody>
              {pools.slice(0, 12).map((p, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={cellTxt}>{p.appPool ?? '—'}</td>
                  <td style={cellNum}>{(p.memoryGb ?? 0).toFixed(2)}</td>
                  <td style={cellNum}>
                    {(p.percentOfTotal ?? 0).toFixed(1)}%
                    {bar(p.percentOfTotal ?? 0, '#2563eb')}
                  </td>
                  <td style={cellNum}>{(p.cpuPct ?? 0).toFixed(1)}</td>
                  <td style={cellNum}>{p.processId ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Top Memory Consumers */}
      <div style={cardStyle}>
        <div style={titleStyle}>
          <i className="ti ti-chart-donut" style={{ marginRight: 4, color: '#F97316' }} />
          Memory Breakdown — who's using RAM ({consumers.length})
        </div>
        {consumers.length === 0 ? (
          <div style={{ color: '#999', fontSize: 11 }}>No memory consumers recorded.</div>
        ) : (
          <table className="smon-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: '#666' }}>
              <th style={cellTxt}>Process / app pool</th>
              <th style={cellTxt}>Category</th>
              <th style={cellNum}>Memory</th>
              <th style={cellNum}>% host</th>
              <th style={cellNum}>#</th>
            </tr></thead>
            <tbody>
              {consumers.slice(0, 12).map((c, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={cellTxt}>{c.name ?? '—'}</td>
                  <td style={cellTxt}>
                    <span style={{ background: '#f3f4f6', padding: '1px 6px',
                                   borderRadius: 8, color: '#374151', fontSize: 10 }}>
                      {c.category ?? 'Other'}
                    </span>
                  </td>
                  <td style={cellNum}>{(c.memoryGb ?? 0).toFixed(2)} GB</td>
                  <td style={cellNum}>
                    {(c.percentOfTotal ?? 0).toFixed(1)}%
                    {bar(c.percentOfTotal ?? 0, '#F97316')}
                  </td>
                  <td style={cellNum}>{c.processCount ?? 1}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* XAF .NET runtimes */}
      <div style={cardStyle}>
        <div style={titleStyle}>
          <i className="ti ti-code-circle-2" style={{ marginRight: 4, color: '#059669' }} />
          ALPACA XAF runtimes ({runtimes.length})
        </div>
        {runtimes.length === 0 ? (
          <div style={{ color: '#999', fontSize: 11 }}>No XAF runtime counters captured (Windows-only).</div>
        ) : (
          <table className="smon-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: '#666' }}>
              <th style={cellTxt}>App pool</th>
              <th style={cellNum}>Gen2 GC</th>
              <th style={cellNum}>LOH MB</th>
              <th style={cellNum}>Threads</th>
              <th style={cellNum}>Handles</th>
              <th style={cellNum}>req/s</th>
              <th style={cellNum}>err/s</th>
            </tr></thead>
            <tbody>
              {runtimes.map((rt, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={cellTxt}>{rt.appPool ?? '—'}</td>
                  <td style={cellNum}>{rt.gen2GcCount ?? 0}</td>
                  <td style={cellNum}>{(rt.lohSizeMb ?? 0).toFixed(1)}</td>
                  <td style={cellNum}>{rt.threadCount ?? 0}</td>
                  <td style={cellNum}>{rt.handleCount ?? 0}</td>
                  <td style={cellNum}>{(rt.requestsPerSec ?? 0).toFixed(1)}</td>
                  <td style={cellNum}>{(rt.errorsPerSec ?? 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Services */}
      <div style={cardStyle}>
        <div style={titleStyle}>
          <i className="ti ti-server-bolt" style={{ marginRight: 4, color: '#9333ea' }} />
          Services ({services.length})
        </div>
        {services.length === 0 ? (
          <div style={{ color: '#999', fontSize: 11 }}>No services recorded.</div>
        ) : (
          <table className="smon-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {services.map((s, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={cellTxt}>{s.name ?? '—'}</td>
                  <td style={{ ...cellTxt, textAlign: 'right',
                               color: s.isOnline ? '#16a34a' : '#dc2626',
                               fontWeight: 600 }}>
                    {s.isOnline ? '● Online' : '○ Offline'}
                  </td>
                  <td style={{ ...cellTxt, color: '#666', fontSize: 10 }}>
                    {s.details ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function ServerHistoryPage() {
  const notify = useNotify();
  const [preset,     setPreset]     = useState<Preset>('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const [rows,       setRows]       = useState<SnapshotRow[]>([]);
  const [summary,    setSummary]    = useState<Summary | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [loadMs,     setLoadMs]     = useState<number | null>(null);
  const [err,        setErr]        = useState<string | null>(null);
  const [capturing,  setCapturing]  = useState(false);
  const [expanded,   setExpanded]   = useState<string | null>(null);   // Oid of expanded row

  const range = useMemo(
    () => buildRange(preset, customFrom, customTo),
    [preset, customFrom, customTo]
  );

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    const t0 = performance.now();
    Promise.all([
      api.get('/api/v1/server/history',         { params: range }),
      api.get('/api/v1/server/history/summary', { params: range }),
    ]).then(([listRes, sumRes]) => {
      if (!alive) return;
      setRows((listRes.data as { data?: SnapshotRow[] }).data ?? []);
      setSummary((sumRes.data as { data?: Summary }).data ?? null);
    }).catch(e => {
      if (!alive) return;
      setErr((e as { message?: string }).message ?? 'Failed to load history.');
    }).finally(() => { if (alive) { setLoadMs(performance.now() - t0); setLoading(false); } });
    return () => { alive = false; };
  }, [range.fromUtc, range.toUtc]);

  // Chart points — x = epoch ms (sort key), y = metric value
  const cpuPts = rows.filter(r => r.cpuPct != null)
    .map(r => ({ x: new Date(r.capturedAt).getTime(), y: r.cpuPct! }));
  const memPts = rows.filter(r => r.memPct != null)
    .map(r => ({ x: new Date(r.capturedAt).getTime(), y: r.memPct! }));

  const dl = (path: string) => {
    // Authenticated download — add token to URL via temporary anchor +
    // axios redirect.  Simpler: just open the URL with the Bearer token in
    // the Authorization header by going through axios then synthesising a
    // download blob — but the simplest cross-origin-safe way is to add
    // the token as a query param OR open in a new tab with credentials.
    const token = getToken();
    const url = `${api.defaults.baseURL ?? ''}${path}&from=${encodeURIComponent(range.fromUtc)}&to=${encodeURIComponent(range.toUtc)}`;
    // Fire the request via axios so the bearer token is attached:
    api.get(path.replace(/^\?/, ''), { params: range, responseType: 'blob' })
      .then(res => {
        const blob = new Blob([res.data as BlobPart],
          { type: (res.headers['content-type'] as string) ?? 'application/octet-stream' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        // Filename from Content-Disposition if present, otherwise from URL
        const cd = res.headers['content-disposition'] as string | undefined;
        const fnMatch = cd?.match(/filename=("?)([^";]+)\1/i);
        link.download = fnMatch?.[2] ?? path.split('/').pop() ?? 'download';
        document.body.appendChild(link); link.click(); link.remove();
        setTimeout(() => URL.revokeObjectURL(link.href), 5000);
      })
      .catch(e => setErr((e as { message?: string }).message ?? 'Download failed.'));
    void url; void token;
  };

  const captureNow = () => {
    setCapturing(true); setErr(null);
    api.post('/api/v1/server/history/capture-now')
      .then(() => {
        // Refresh to show the just-captured row + flash a success toast.
        // We also force preset to 'hour' if the user is on a range that
        // wouldn't show the new row (e.g. 'yesterday'), so the snapshot
        // they just took is immediately visible.
        return Promise.all([
          api.get('/api/v1/server/history',         { params: range }),
          api.get('/api/v1/server/history/summary', { params: range }),
        ]).then(([listRes, sumRes]) => {
          setRows((listRes.data as { data?: SnapshotRow[] }).data ?? []);
          setSummary((sumRes.data as { data?: Summary }).data ?? null);
          notify.success('Snapshot captured.');
        });
      })
      .catch(e => notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message
        ?? (e as { message?: string }).message
        ?? 'Capture failed.'))
      .finally(() => setCapturing(false));
  };

  // Shared-grid columns for the snapshot history.  A leading chevron column
  // mirrors the expand/collapse affordance; CPU%/Mem% keep their ≥80% red
  // highlight via a custom renderer (text mirrors the value for filter/sort).
  const expandedRow = useMemo(() => rows.find(r => r.oid === expanded) ?? null, [rows, expanded]);
  const histCols: AppColumn<SnapshotRow>[] = [
    { field: '_exp', header: '', width: 28, align: 'center', sortable: false, filterable: false, noExport: true,
      text: () => '',
      render: r => <i className={expanded === r.oid ? 'ti ti-chevron-down' : 'ti ti-chevron-right'} style={{ color: '#666' }} /> },
    { field: 'capturedAt', header: 'Captured', type: 'datetime', width: 180,
      render: r => fmtLocal(r.capturedAt), text: r => fmtLocal(r.capturedAt) },
    { field: 'cpuPct', header: 'CPU %', type: 'number', width: 90,
      value: r => r.cpuPct ?? 0,
      render: r => <span style={{ color: (r.cpuPct ?? 0) >= 80 ? '#dc2626' : '#222' }}>{(r.cpuPct ?? 0).toFixed(1)}%</span>,
      text: r => `${(r.cpuPct ?? 0).toFixed(1)}%` },
    { field: 'memPct', header: 'Mem %', type: 'number', width: 90,
      value: r => r.memPct ?? 0,
      render: r => <span style={{ color: (r.memPct ?? 0) >= 80 ? '#dc2626' : '#222', fontWeight: (r.memPct ?? 0) >= 80 ? 700 : 400 }}>{(r.memPct ?? 0).toFixed(1)}%</span>,
      text: r => `${(r.memPct ?? 0).toFixed(1)}%` },
    { field: 'memUsedGb', header: 'Mem (GB)', type: 'number', width: 120,
      value: r => r.memUsedGb ?? 0,
      render: r => <>{(r.memUsedGb ?? 0).toFixed(2)} / {(r.memTotalGb ?? 0).toFixed(2)}</>,
      text: r => `${(r.memUsedGb ?? 0).toFixed(2)} / ${(r.memTotalGb ?? 0).toFixed(2)}` },
    { field: 'diskUsedGb', header: 'Disk (GB)', type: 'number', width: 120,
      value: r => r.diskUsedGb ?? 0,
      render: r => <>{(r.diskUsedGb ?? 0).toFixed(0)} / {(r.diskTotalGb ?? 0).toFixed(0)}</>,
      text: r => `${(r.diskUsedGb ?? 0).toFixed(0)} / ${(r.diskTotalGb ?? 0).toFixed(0)}` },
    { field: 'warnings', header: 'Warnings',
      render: r => <span style={{ color: '#9a3412' }}>{r.warnings ?? ''}</span>,
      text: r => r.warnings ?? '' },
  ];

  // Time-range presets — shared .sa-filter-btn chips (same treatment as the
  // Audit Trail operation chips; theme-token active state, no per-form style).
  const presetBtn = (key: Preset, label: string) => (
    <button key={key} onClick={() => setPreset(key)}
            className={`sa-filter-btn${preset === key ? ' active' : ''}`}>
      {label}
    </button>
  );

  return (
    // Same shell-style as the live Server Monitor page: takes the available
    // height inside the form slot, owns its own scrollbar, normal padding —
    // sits right next to the navigation panel, not edge-to-edge.
    <div
      className="sh-scroll"
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        background: 'var(--ai-bg)',
        fontFamily: 'var(--ai-font)',
      }}
    >
      <style>{`
        .sh-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
        .sh-scroll::-webkit-scrollbar-track { background: transparent; }
        .sh-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 5px; }
        .sh-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    <div style={{ padding: '14px 20px' }}>
      <FormTitle pageId="servermon-history" loadMs={loadMs} loading={loading} />
      <div style={{ fontSize: 12, color: '#666', marginBottom: 14 }}>
        Hourly snapshots persisted by the API.  Use a preset or pick a custom
        range, then download as Excel / CSV / PDF for analysis or sharing.
      </div>

      {/* Shared toolbar — read-only dashboard (no CRUD).  Time-range presets,
          custom-range inputs and the capture / Excel / PDF download buttons live
          in the left slot (these are form-specific); the primary CSV download is
          the canonical "Export to".  No Refresh — the page reloads on range
          change. */}
      <div style={{ marginBottom: 12 }}>
        <AppToolbar
          left={
            <>
              {presetBtn('hour',      'Last hour')}
              {presetBtn('today',     'Today')}
              {presetBtn('yesterday', 'Yesterday')}
              {presetBtn('7d',        'Last 7 days')}
              {presetBtn('30d',       'Last 30 days')}
              {presetBtn('custom',    'Custom')}
              {preset === 'custom' && (
                <>
                  <input type="datetime-local" value={customFrom}
                         onChange={e => setCustomFrom(e.target.value)}
                         style={{ padding: '3px 6px', fontSize: 12,
                                  border: '1px solid var(--ai-line)', borderRadius: 3 }} />
                  <span>→</span>
                  <input type="datetime-local" value={customTo}
                         onChange={e => setCustomTo(e.target.value)}
                         style={{ padding: '3px 6px', fontSize: 12,
                                  border: '1px solid var(--ai-line)', borderRadius: 3 }} />
                </>
              )}
              <span style={{ flex: 1 }} />
              {/* Shared .ca-btn actions (BO UI Standard — no per-form button
                  colours); existing capture/export logic unchanged. */}
              <button className="ca-btn" onClick={captureNow} disabled={capturing}>
                <BusyIcon busy={capturing} icon="ti-camera" />
                &nbsp;{capturing ? 'Capturing…' : 'Snapshot now'}
              </button>
              <button className="ca-btn" onClick={() => dl('/api/v1/server/history/export.xlsx')}>
                <i className="ti ti-file-spreadsheet" />&nbsp;Excel
              </button>
              <button className="ca-btn" onClick={() => dl('/api/v1/server/history/export.pdf')}>
                <i className="ti ti-file-type-pdf" />&nbsp;PDF
              </button>
            </>
          }
          exportData={{ filename: 'server-history', ...gridExportMatrix(rows, histCols) }}
        />
      </div>

      {/* Error banner */}
      {err && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca',
                      color: '#991b1b', padding: '8px 12px', borderRadius: 4,
                      fontSize: 12, marginBottom: 12 }}>
          {err}
        </div>
      )}
      {/* Summary card */}
      {summary && (
        <div style={{ background: 'var(--ai-card)', border: '1px solid var(--ai-line)',
                      borderRadius: 10, boxShadow: 'var(--ai-shadow-card)',
                      padding: '12px 16px', marginBottom: 14,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: 12, fontSize: 12 }}>
          <div>
            <div style={{ color: '#666' }}>Samples</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{summary.sampleCount}</div>
          </div>
          <div>
            <div style={{ color: '#666' }}>Peak CPU</div>
            <div style={{ fontSize: 18, fontWeight: 700,
                          color: summary.peakCpuPct >= 80 ? '#dc2626' : '#111' }}>
              {summary.peakCpuPct.toFixed(1)}%
            </div>
            <div style={{ fontSize: 10, color: '#888' }}>
              at {fmtLocal(summary.peakCpuAtUtc)}
            </div>
          </div>
          <div>
            <div style={{ color: '#666' }}>Peak Memory</div>
            <div style={{ fontSize: 18, fontWeight: 700,
                          color: summary.peakMemPct >= 80 ? '#dc2626' : '#111' }}>
              {summary.peakMemPct.toFixed(1)}%
            </div>
            <div style={{ fontSize: 10, color: '#888' }}>
              at {fmtLocal(summary.peakMemAtUtc)}
            </div>
          </div>
          <div>
            <div style={{ color: '#666' }}>Avg CPU / Mem · Warnings</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {summary.avgCpuPct.toFixed(1)}% / {summary.avgMemPct.toFixed(1)}% ·{' '}
              <span style={{ color: summary.warningCount > 0 ? '#dc2626' : '#666' }}>
                {summary.warningCount} warnings
              </span>
            </div>
            <div style={{ fontSize: 10, color: '#888' }}>
              Top consumer: {summary.topMemoryConsumer ?? '—'}
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div style={{ background: 'var(--ai-card)', border: '1px solid var(--ai-line)',
                    borderRadius: 10, boxShadow: 'var(--ai-shadow-card)',
                    padding: '12px 16px', marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#1F4E79', marginBottom: 4 }}>
          CPU % over time
        </div>
        <MiniLineChart points={cpuPts} color="#2563eb" max={100} label="%" />
        <div style={{ fontSize: 12, fontWeight: 600, color: '#1F4E79', marginTop: 14, marginBottom: 4 }}>
          Memory % over time
        </div>
        <MiniLineChart points={memPts} color="#ea580c" max={100} label="%" />
      </div>

      {/* Snapshot table — shared <AppDataGrid> (styling, per-column filters,
          sort, footer / pagination, load-time all standardised).  Click a row
          to expand the per-snapshot breakdown shown directly below the grid. */}
      <div style={{ background: 'var(--ai-card)', border: '1px solid var(--ai-line)',
                    borderRadius: 10, boxShadow: 'var(--ai-shadow-card)', overflow: 'hidden' }}>
        <AppDataGrid<SnapshotRow>
          rows={rows}
          columns={histCols}
          getRowId={r => r.oid}
          loading={loading}
          loadMs={loadMs}
          selectedRowId={expanded}
          onRowClick={r => setExpanded(expanded === r.oid ? null : r.oid)}
          emptyText='No snapshots in this range yet. The capture worker runs every hour; click "Snapshot now" for an immediate one.'
        />
        {expandedRow && (
          <div style={{ padding: '8px 14px 14px 14px', background: 'var(--ai-header-bg)',
                        borderTop: '1px solid var(--ai-line)' }}>
            <SnapshotDetail row={expandedRow} />
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
